import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faExclamationTriangle } from "@fortawesome/pro-light-svg-icons";

import {
    deleteGisHierarchyItem,
    deleteGisHierarchyLayer,
    getAllGisHierarchyItemsByHierarchyId,
} from "services/requests/v1/gishierarchyitem";

import NestedList, { NestedListDataType } from "./nested-list.component";

import ModalContentLayout from "./modal-content-layout.component";
import { useGisLayersTreeContext } from "storage/context/gis-layers-tree-hierarchy-context";
import { useTranslation } from "utils/hooks/use-translation.hook";

type ConfirmationDeleteType = {
    closeModalFn: () => void;
};

const ConfirmationDeleteGisHierarchy = ({
    closeModalFn,
}: ConfirmationDeleteType) => {
    const translate = useTranslation();

    const {
        gisHierarchySelected,
        setGisHierarchyId,
        setGisHierarchySelected,
        subscribeContent,
        unsubscribeContent,
        contentName,
        setError,
    } = useGisLayersTreeContext();

    const childsToBeDeleted: NestedListDataType[] = [];

    gisHierarchySelected.forEach((hierarchy) => {
        if (hierarchy.itemType) return;

        childsToBeDeleted.push({
            label: hierarchy.name,
            key: hierarchy.name,
            content: hierarchy.gisLayers!.map(({ alias, id }) => ({
                label: alias,
                key: id,
            })),
        });
    });

    const handleDelete = async () => {
        subscribeContent(contentName);

        Promise.all(
            gisHierarchySelected.map((hierarchy) => {
                if (hierarchy.itemType) {
                    return deleteGisHierarchyLayer(Math.abs(hierarchy.id!));
                } else {
                    return deleteGisHierarchyItem(hierarchy.id!);
                }
            })
        )
            .then(() => unsubscribeContent(contentName, true))
            .catch(() => setError(contentName))
            .finally(async () => {
                const { data } = await getAllGisHierarchyItemsByHierarchyId(1);

                setGisHierarchyId(data);
                closeModalFn();
                setGisHierarchySelected([]);
            });
    };

    const defineDeleteMessage = () => {
        if (!gisHierarchySelected) return "a";

        if (gisHierarchySelected[0].itemType) {
            return "app.hierarchy.delete_hierarchy_layer_message";
        }

        if (childsToBeDeleted.length > 1) {
            return "app.hierarchy.delete_hierarchy_group_message";
        }

        return "app.hierarchy.delete_hierarchy_single_group_message";
    };

    return (
        <ModalContentLayout
            firstButtonTitle={translate("app.gis.cancel")}
            secondButtonTitle={translate("app.Components.warning.delete")}
            onFirstButtonClick={closeModalFn}
            onSecondButtonClick={handleDelete}
            firstButtonStatus="enabled"
            secondButtonStatus={"enabled"}
        >
            <div className="c-confirmation-modal">
                <FontAwesomeIcon
                    className="c-confirmation-modal__icon"
                    icon={faExclamationTriangle}
                />
                <div className="c-confirmation-modal__message-container">
                    <span className="c-confirmation-modal__message">
                        {translate(defineDeleteMessage())}
                    </span>

                    <div className="c-confirmation-modal__list ">
                        <NestedList data={childsToBeDeleted} />
                    </div>
                </div>
            </div>
        </ModalContentLayout>
    );
};

export default ConfirmationDeleteGisHierarchy;
